import { useQuery } from '@tanstack/react-query';

import type { ClientId, SessionChargeId } from '@nesso/ledger-contracts/ids';
import {
    SheetBody,
    SheetClose,
    SheetCloseButton,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from '@nesso/ui/primitives/sheet';

import { unpaidSessionChargeListOptions } from '@/entities/session-charge';
import { formatCurrencyFromCents } from '@/shared/lib/format-utils';
import { CenteredSpinner } from '@/shared/ui/indicators/centered-spinner';
import { SheetBreadcrumb } from '@/shared/ui/panels/sheet-breadcrumb';

import { RecordPaymentSheetForm } from './record-payment-sheet-content/record-payment-sheet-form';

type Props = {
    clientId: ClientId;
    clientName: string;
    selectedSessionChargeId: SessionChargeId | null;
    onClose: () => void;
    onBreadcrumbNavigate: (navigate: () => void) => void;
    onPendingChange: (pending: boolean) => void;
};

export function RecordPaymentSheetContent({
    clientId,
    clientName,
    selectedSessionChargeId,
    onClose,
    onBreadcrumbNavigate,
    onPendingChange,
}: Props) {
    const unpaidList = useQuery({
        ...unpaidSessionChargeListOptions(clientId),
        refetchOnMount: 'always',
        gcTime: 0,
    });
    const unpaid = unpaidList.data?.items;
    const outstandingCents = unpaid?.reduce((sum, charge) => sum + charge.balanceCents, 0) ?? 0;

    return (
        <>
            <SheetHeader variant="breadcrumbs" className="shrink-0">
                <SheetBreadcrumb
                    current="Add Payment"
                    getLabel={(sheet) =>
                        sheet.type === 'clients/details' && sheet.data.id === clientId
                            ? clientName
                            : undefined
                    }
                    onNavigate={onBreadcrumbNavigate}
                />
                <SheetClose asChild>
                    <SheetCloseButton className="ml-auto shrink-0" />
                </SheetClose>
            </SheetHeader>
            <SheetHeader variant="chart">
                <SheetTitle>Add Payment</SheetTitle>
                <SheetDescription>
                    {unpaid && `${formatCurrencyFromCents(outstandingCents)} outstanding`}
                </SheetDescription>
            </SheetHeader>
            {unpaid && unpaidList.isFetchedAfterMount ? (
                <RecordPaymentSheetForm
                    clientId={clientId}
                    unpaid={unpaid}
                    selectedSessionChargeId={selectedSessionChargeId}
                    onClose={onClose}
                    onPendingChange={onPendingChange}
                />
            ) : (
                <SheetBody className="flex flex-1 flex-col">
                    {unpaidList.isError ? (
                        <p className="text-base">Couldn't load sessions</p>
                    ) : (
                        <CenteredSpinner label="Loading sessions" />
                    )}
                </SheetBody>
            )}
        </>
    );
}
